import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSimUsersByCompany } from '../Services/APIs/dashboard';

const Patients = () => {
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [selectedIntentFilter, setSelectedIntentFilter] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [patientsData, setPatientsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({});
    const [counts, setCounts] = useState({});
    const navigate = useNavigate();

    const filters = ['New', 'Contacted', 'High Intent', 'Consultation Scheduled'];
    const ITEMS_PER_PAGE = 8;

    // Map filter display names to API status values
    const filterStatusMap = {
        'New': 'new',
        'Contacted': 'contacted',
        'Consultation Scheduled': 'consultation_scheduled',
        'High Intent': '' // High Intent is handled separately by is_high_intent flag
    };

    // Fetch patients data from API
    useEffect(() => {
        const fetchPatients = async () => {
            setLoading(true);
            setError(null);
            try {
                const statusValue = selectedFilter && selectedFilter !== 'High Intent'
                    ? filterStatusMap[selectedFilter]
                    : '';
                const isHighIntent = selectedFilter === 'High Intent' || selectedIntentFilter;

                const response = await getSimUsersByCompany({
                    status: statusValue,
                    is_high_intent: isHighIntent,
                    page: currentPage,
                    limit: ITEMS_PER_PAGE,
                });

                if (response.data && response.data.users) {
                    // Transform API response to match component's expected format
                    const transformedUsers = response.data.users.map((user) => ({
                        _id: user._id,
                        uid: user.patient_UUID,
                        patient: `${user.first_name} ${user.last_name}`,
                        email: user.email,
                        date: new Date(user.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                        }),
                        statuses: buildStatuses(user),
                        phone: user.phone,
                        patient_UUID: user.patient_UUID,
                        raw: user, // Store raw data for reference
                        uids: user.uids,
                        uidCount: Array.isArray(user.uids) ? user.uids.length : 0


                    }));

                    setPatientsData(transformedUsers);
                    setPagination(response.data.pagination || {});
                    setCounts(response.data.counts || {});
                }
            } catch (err) {
                console.error('Error fetching patients:', err);
                setError(err?.response?.data?.message || 'Failed to fetch patients. Please try again.');
                setPatientsData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPatients();
    }, [selectedFilter, selectedIntentFilter, currentPage]);

    // Build status array from user object
    const buildStatuses = (user) => {
        const statuses = [];

        if (user.status === 'new') {
            statuses.push('New');
        } else if (user.status === 'contacted') {
            statuses.push('Contacted');
        } else if (user.status === 'consultation_scheduled') {
            statuses.push('Consultation Scheduled');
        }

        if (user.is_high_intent) {
            statuses.push('High Intent');
        }

        return statuses.length > 0 ? statuses : [''];
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'New':
                return 'bg-[#C3D8CC] text-[#2F4437]';
            case 'Contacted':
                return 'bg-[#92A395] text-[#2F3A32]';
            case 'High Intent':
                return 'bg-[#D3FDD9] text-[#2F5E38]';
            case 'Consultation Scheduled':
                return 'bg-[#58665A] text-[#D0D9D2]';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const handleFilterClick = (filter) => {
        if (filter === 'High Intent') {
            setSelectedIntentFilter(!selectedIntentFilter);
            setSelectedFilter(null);
        } else {
            setSelectedFilter(selectedFilter === filter ? null : filter);
            setSelectedIntentFilter(false);
        }
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleClearFilters = () => {
        setSelectedFilter(null);
        setSelectedIntentFilter(false);
        setCurrentPage(1); // Reset to first page when filters are cleared
    };

    return (
        <main className="flex-1 overflow-auto">
            <div className="p-4 md:p-8 max-w-7xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl text-[#2D2D2D] tracking-tight mb-4">Patients</h1>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-[#6B6B6B] uppercase tracking-wider mr-2">Filter by:</span>
                        {filters.map((filter) => (
                            <span
                                key={filter}
                                data-slot="badge"
                                className={`inline-flex items-center justify-center rounded-md text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive overflow-hidden [a&]:hover:bg-primary/90 bg-white text-[#6B6B6B] hover:bg-[#F5F3F0] border border-[#E8E6E3] cursor-pointer transition-colors px-3 py-1 ${(filter === 'High Intent' && selectedIntentFilter) || (selectedFilter === filter)
                                    ? 'bg-[#F5F3F0] border-blue-300'
                                    : ''
                                    }`}
                                onClick={() => handleFilterClick(filter)}
                            >
                                {filter}
                            </span>
                        ))}
                        {(selectedFilter || selectedIntentFilter) && (
                            <span
                                className=" font-dashboard inline-flex items-center justify-center rounded-md text-xs font-medium w-fit whitespace-nowrap shrink-0 cursor-pointer transition-colors px-3 py-1 text-[#6B6B6B] hover:text-[#2D2D2D] gap-1"
                                onClick={handleClearFilters}
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                CLEAR
                            </span>
                        )}
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-sm border border-[#E8E6E3] overflow-hidden">
                    <div className="px-4 md:px-6 py-5 border-b border-[#E8E6E3]">
                        <div className="flex justify-between items-center gap-3">
                            <h2 className="text-lg text-[#2D2D2D]">Recent Smile Previews</h2>
                            {pagination.total_users !== undefined && (
                                <p className="text-xs text-[#6B6B6B]">
                                    Total: {pagination.total_users} users
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D2D2D]"></div>
                        </div>
                    )}

                    {/* Table */}
                    {!loading && (
                        <>
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full min-w-[760px]">
                                    <thead>
                                        <tr className="border-b border-[#E8E6E3]">
                                            <th className="text-left px-6 py-4 text-xs text-[#6B6B6B] uppercase tracking-wider">
                                                Patient
                                            </th>
                                            <th className="text-left px-6 py-4 text-xs text-[#6B6B6B] uppercase tracking-wider">
                                                Email
                                            </th>
                                            <th className="text-left px-6 py-4 text-xs text-[#6B6B6B] uppercase tracking-wider">
                                                Phone
                                            </th>
                                            <th className="text-left px-6 py-4 text-xs text-[#6B6B6B] uppercase tracking-wider">
                                                Date
                                            </th>
                                            <th className="text-left px-6 py-4 text-xs text-[#6B6B6B] uppercase tracking-wider">
                                                Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#E8E6E3]">
                                        {patientsData.length > 0 ? (
                                            patientsData.map((patient) => (
                                                <tr
                                                    key={patient.uid}
                                                    className="hover:bg-[#FAFAF9] cursor-pointer transition-colors"
                                                    onClick={() =>
                                                        navigate(`/dashboard/patients/${patient.uid}`, {
                                                            state: { patient },
                                                        })
                                                    }
                                                >
                                                    <td className="px-6 py-4 text-sm flex text-[#2D2D2D]">{patient.patient}
                                                        {patient.uidCount > 1 && (
                                                            <div className="group relative ms-2">
                                                                <span className="inline-flex items-center justify-center h-[18px] min-w-[18px] px-[4px] rounded-full bg-[#E8F5E8] text-[#2F5E38] font-['Aeonik:Medium',sans-serif] text-[10px] font-medium cursor-help">
                                                                    {patient.uidCount}
                                                                </span>
                                                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                                                    Total checks taken
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-[#6B6B6B]">{patient.email}</td>
                                                    <td className="px-6 py-4 text-sm text-[#6B6B6B]">{patient.phone}</td>
                                                    <td className="px-6 py-4 text-sm text-[#6B6B6B]">{patient.date}</td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            {patient.statuses.map(
                                                                (status) =>
                                                                    status && (
                                                                        <span
                                                                            key={status}
                                                                            data-slot="badge"
                                                                            className={`inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent [a&]:hover:bg-secondary/90 ${getStatusColor(
                                                                                status
                                                                            )} hover:bg-[#C3D8CC] border-0`}
                                                                        >
                                                                            {status}
                                                                        </span>
                                                                    )
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : !loading ? (
                                            <tr>
                                                <td colSpan="5" className="px-6 py-4 text-sm text-[#6B6B6B] text-center">
                                                    {error ? 'Unable to load patients' : 'No patients found with the selected filter.'}
                                                </td>
                                            </tr>
                                        ) : null}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile card list — five columns don't fit a phone */}
                            <div className="md:hidden divide-y divide-[#E8E6E3]">
                                {patientsData.length > 0 ? (
                                    patientsData.map((patient) => (
                                        <div
                                            key={patient.uid}
                                            onClick={() =>
                                                navigate(`/dashboard/patients/${patient.uid}`, {
                                                    state: { patient },
                                                })
                                            }
                                            className="flex items-center justify-between gap-3 px-4 py-3.5 hover:bg-[#FAFAF9] active:bg-[#F3F3F0] cursor-pointer transition-colors"
                                        >
                                            <div className="flex flex-col gap-1.5 min-w-0">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="text-sm text-[#2D2D2D] truncate">{patient.patient}</span>
                                                    {patient.uidCount > 1 && (
                                                        <span className="inline-flex items-center justify-center h-[18px] min-w-[18px] px-[4px] rounded-full bg-[#E8F5E8] text-[#2F5E38] font-['Aeonik:Medium',sans-serif] text-[10px] font-medium shrink-0">
                                                            {patient.uidCount}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-xs text-[#6B6B6B] truncate">{patient.email}</span>
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    {patient.statuses.map(
                                                        (status) =>
                                                            status && (
                                                                <span
                                                                    key={status}
                                                                    className={`inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 border-0 ${getStatusColor(status)}`}
                                                                >
                                                                    {status}
                                                                </span>
                                                            )
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-[10px] text-[#ADADAD] shrink-0">{patient.date}</span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="px-4 py-8 text-sm text-[#6B6B6B] text-center">
                                        {error ? 'Unable to load patients' : 'No patients found with the selected filter.'}
                                    </div>
                                )}
                            </div>

                            {/* Pagination Controls */}
                            {pagination && pagination.total_pages > 1 && (
                                <div className="px-4 md:px-6 py-4 border-t border-[#E8E6E3] flex items-center justify-between gap-3">
                                    <div className="text-xs text-[#6B6B6B]">
                                        Page {pagination.current_page} of {pagination.total_pages}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                            disabled={!pagination.has_prev}
                                            className="px-3 py-1 text-xs font-medium rounded-md bg-white border border-[#E8E6E3] text-[#2D2D2D] hover:bg-[#F5F3F0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                            disabled={!pagination.has_next}
                                            className="px-3 py-1 text-xs font-medium rounded-md bg-white border border-[#E8E6E3] text-[#2D2D2D] hover:bg-[#F5F3F0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </main>
    );
};

export default Patients;
